import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  FORGOT_PASSWORD_FAILURE,
  FORGOT_PASSWORD_REQUEST,
  CHANGE_PASSWORD_REQUEST,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAILURE,
  CLEAR_VALIDATION_ERRORS,
  LOGOUT_REQUEST,
  GET_PROFILE_DATA,
  SET_CURRENT_CODE,
  SET_CURRENT_PHONE,
  ADD_RESEND,
  SET_PHONE,
  RESET_RESEND_COUNT,
  TWO_FACTOR_ERROR,
  SET_CURRENT_BLOODBANK,
  SET_TO_ALL_BLOODBANKS,
  SET_PASSWORDS,
  UPDATE_TOKEN,
  SET_2FA,
  FINALIZE_LOGIN_REQUEST,
  FINALIZE_LOGIN_SUCCESS,
  FINALIZE_LOGIN_FAILURE,
  SET_CURRENT_EMAIL,
  SET_FIREWALL_ERROR,
} from './actionTypes';
import { createAction } from 'redux-actions';
import firebase from '../../services/firebase';
import redirect from '../../services/redirect';
import { addPhoneRequest ,apiForgotPassword,getCustomToken,getPasswordPolicy,setBloodBanks,signIn} from '../../services/fetch';
import routes from '../../constants/routes';
import * as dateFns from "date-fns";
import {
  duplicatePasswordsError,
  getForgotPasswordValidationMessage,
  getLoginValidationMessage,
  getNewPasswordValidationMessage,
  passwordValidationError,
} from "../../services/validation";
import authCodes, { loginFailureReason } from '../../constants/authCodes';
import { sendErrorLog, sendSigninAttemptLog } from "../logging/actions";
import {toast} from "react-toastify";
import { getUserIp, checkExternalUrlAccess } from '../../services/helpers';
import * as CryptoJS from 'crypto-js'
import { db } from '../../services/firebase';

export const loginRequest = createAction(LOGIN_REQUEST);
export const loginSuccess = createAction(LOGIN_SUCCESS);
export const loginFailure = createAction(LOGIN_FAILURE);
export const finalizeLoginRequest = createAction(FINALIZE_LOGIN_REQUEST);
export const finalizeLoginSuccess = createAction(FINALIZE_LOGIN_SUCCESS);
export const finalizeLoginFailure = createAction(FINALIZE_LOGIN_FAILURE);
export const updateToken = createAction(UPDATE_TOKEN);
export const forgotPasswordRequest = createAction(FORGOT_PASSWORD_REQUEST);
export const forgotPasswordFailure = createAction(FORGOT_PASSWORD_FAILURE);
export const changePasswordRequest = createAction(CHANGE_PASSWORD_REQUEST);
export const changePasswordSuccess = createAction(CHANGE_PASSWORD_SUCCESS);
export const changePasswordFailure = createAction(CHANGE_PASSWORD_FAILURE);
export const clearValidationErrors = createAction(CLEAR_VALIDATION_ERRORS);
export const userLogoutRequest = createAction(LOGOUT_REQUEST);
export const getProfileData = createAction(GET_PROFILE_DATA);
export const setCurrentCode = createAction(SET_CURRENT_CODE);
export const setCurrentPhone = createAction(SET_CURRENT_PHONE);
export const addResend = createAction(ADD_RESEND);
export const setPhone = createAction(SET_PHONE);
export const resetResendCount = createAction(RESET_RESEND_COUNT);
export const twoFactorError = createAction(TWO_FACTOR_ERROR);
export const setCurrentBloodbank = createAction(SET_CURRENT_BLOODBANK);
export const setToAllBloodbanks = createAction(SET_TO_ALL_BLOODBANKS);
export const setPasswords = createAction(SET_PASSWORDS);
export const setIsInTwoFactor = createAction(SET_2FA);
export const setCurrentEmail = createAction(SET_CURRENT_EMAIL);
export const setFirewallError = createAction(SET_FIREWALL_ERROR);

export const handleSetCurrentBloodbank = (bloodbank) => (dispatch) => {
  dispatch(setCurrentBloodbank(bloodbank));
  const dateFormat = "yyyy-MM-dd";
  const formattedDate = dateFns.format(new Date(), dateFormat);
  redirect(`${routes.DASHBOARD_DAY_ROUTE}/${formattedDate}`);
};

export const clearAuthValidationErrors = () => (dispatch) => {
  dispatch(clearValidationErrors());
};

const redirectToCurrentDayPage = () => {
  const dateFormat = "yyyy-MM-dd";
  const formattedDate = dateFns.format(new Date(), dateFormat);
  redirect(`${routes.DASHBOARD_DAY_ROUTE}/${formattedDate}`);
};

export const getIdToken = (redirect=false) => (dispatch) => {
  return firebase.auth().currentUser.getIdTokenResult(true)
    .then(res => {
      dispatch(loginSuccess(res));
      dispatch(finalizeLoginSuccess());
      if (redirect) {
        redirectToCurrentDayPage();
      }
      return res.token;
    })
    .catch(err => {
      dispatch(sendErrorLog(err.message, err.code));
      dispatch(loginFailure(getLoginValidationMessage(err.code)))
    });
};

const isTokenRefreshNeeded = (idToken, expTimestamp) => {
  if (idToken && expTimestamp) {
    const expiresIn = expTimestamp - Date.now();
    if (expiresIn <= 0) {
      return true;
    }
    return false;
  }
  return true;
};

export const getValidIdToken = () => (dispatch, getState) => {
  const { idToken, expTimestamp } = getState()?.authReducer;

  if (isTokenRefreshNeeded(idToken, expTimestamp)) {
    return dispatch(getIdToken());
  } else {
    return idToken;
  }
};

const fillBloodBanks = async (dispatch, {access, roleName, profileStorageRef, id}, phone) => {
  const receivedBloodbanks = access;
  const preparedBloodbanks = [];

  for (let index = 0; index < receivedBloodbanks.length; index++) {
    const { bloodbankId, name = null } = receivedBloodbanks[index];
    if (name) {
      preparedBloodbanks.push({ bloodbankId, bloodbankName: name });
    } else {
      const bloodbankRef = firebase.firestore().collection('bloodbanks').doc(bloodbankId);
      const doc = await bloodbankRef.get();
      const bloodbankName = doc.data()?.name;
      preparedBloodbanks.push({ bloodbankId, bloodbankName });
    }
  }

  dispatch(setToAllBloodbanks(preparedBloodbanks));
  dispatch(setCurrentBloodbank(preparedBloodbanks[0]));
  dispatch(getProfileData({ roleName, profileStorageRef, phone, uid: id }));
}

function getRecaptchaVerifier() {
  if (!window.recaptchaVerifier) {
    window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
      "recaptcha-container",
      {
        size: "invisible",
      }
    );

    window.recaptchaVerifier.render().then((widgetId) => {
      window.recaptchaWidgetId = widgetId;
    });
  }

  return window.recaptchaVerifier;
}

export const login = ({ email, password }) => async (dispatch) => {
  dispatch(loginRequest());
  dispatch(setCurrentEmail(email));

  let ip = await getUserIp();

  signIn(email, password)
    .then(async (res) => {
      try {
        await firebase.auth().signInWithEmailAndPassword(email, password);
      } catch (error) {
        if (error.code === 'auth/multi-factor-auth-required') {
          dispatch(setIsInTwoFactor(true))
          redirect(`${routes.TWO_FACTOR_AUTH_ROUTE}`);
          firebase.auth().useDeviceLanguage();

          const resolver = error.resolver;
          const hints = resolver.hints;

          const phoneHint = hints.find(hint => hint.factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID);

          if (phoneHint) {
            const phoneInfoOptions = {
              multiFactorHint: phoneHint,
              session: resolver.session
            };

            const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();

            try {
              const verifier = getRecaptchaVerifier();
              const verificationId = await phoneAuthProvider.verifyPhoneNumber(
                phoneInfoOptions,
                verifier
              );

              dispatch(setCurrentCode({ verificationId, resolver }));
              dispatch(setIsInTwoFactor(true))
              redirect(`${routes.TWO_FACTOR_AUTH_ROUTE}`);
              firebase.auth().useDeviceLanguage();

            } catch (smsError) {
              console.error("Error sending SMS:", smsError);
              dispatch(loginFailure(smsError));
            }
          } else {
            dispatch(loginFailure({ message: "MFA required, but no phone number is enrolled." }));
          }
        } else {
          const blockedUrls = await checkExternalUrlAccess();
          if (blockedUrls.length > 0) {
            dispatch(setFirewallError(blockedUrls));
          } else {
            dispatch(loginFailure(error));
          }
        }
      }
    })
    .catch(async (err) => {
      console.log(err)
      dispatch(sendSigninAttemptLog({ email, status: 'failure', ip, failureReason: loginFailureReason.WRONG_CREDENTIALS, type: 'login' }));
      dispatch(sendErrorLog(err.message, err.code));

      const blockedUrls = await checkExternalUrlAccess();
      if (blockedUrls.length > 0) {
        dispatch(setFirewallError(blockedUrls));
      } else {
        dispatch(loginFailure());
      }
    });
};

export const confirmCode = (code) => async (dispatch, getState) => {
  dispatch(finalizeLoginRequest());
  let ip = await getUserIp();
  const { verificationId, resolver } = getState().authReducer.currentCode;
  const { currentEmail } = getState().authReducer;

  if (!verificationId || !resolver) {
    console.error("MFA session info is missing. Redirecting to login.");
    window.location.href = "/login";
    return;
  }

  const credential = firebase.auth.PhoneAuthProvider.credential(
    verificationId,
    code
  );

  const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(credential);

  try {
    const userCredential = await resolver.resolveSignIn(multiFactorAssertion);

    const phone = userCredential.user.phoneNumber;
    const uid = userCredential.user.uid;
    const doc = await db.collection("admins").doc(uid).get();
    const access = doc.data().access;
    const profileStorageRef = doc.data().profileStorageRef;
    const roleName = doc.data().roleName;
    const email = userCredential.user.email;

    fillBloodBanks(dispatch, {access, roleName, profileStorageRef, id: uid }, phone);

    dispatch(sendSigninAttemptLog({ email, status: 'success', ip, type: 'login' }));
    dispatch(getIdToken(true));

    // const user = firebase.auth().currentUser;
    // console.log(firebase.auth.PhoneAuthProvider.PROVIDER_ID);

    // if(firebase.auth.PhoneAuthProvider.PROVIDER_ID) {
    //   await user.unlink(firebase.auth.PhoneAuthProvider.PROVIDER_ID);
    //   console.log("Phone unlinked successfully")
    // }


  } catch (err) {
    dispatch(sendSigninAttemptLog({email: currentEmail, status: 'failure', failureReason: loginFailureReason.TWO_FACTOR_WRONG_CODE, ip, type: 'login' }));
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError());
    dispatch(finalizeLoginFailure());
  }
};

export const resendCode = () => (dispatch, getState) => {

  const { phone } = getState().authReducer;

  firebase.auth().useDeviceLanguage();



  firebase.auth().signInWithPhoneNumber(`+${phone}`, getRecaptchaVerifier())
  .then((res) => {
    dispatch(setCurrentCode(res));
    dispatch(addResend());
    redirect(`${routes.TWO_FACTOR_AUTH_CODE_SENT_ROUTE}`);
  })
  .catch((err) => {
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError())
  })
}


export const confirmCodeMobile = (code) => async (dispatch, getState) => {

  const { userId, currentPhone } = getState().authReducer;
  const user = firebase.auth().currentUser;
  try {
    const session = await user.multiFactor.getSession();

    const phoneInfoOptions = {
      phoneNumber: currentPhone,
      session,
    };

    const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      getRecaptchaVerifier()
    );

    const credential = firebase.auth.PhoneAuthProvider.credential(
      verificationId,
      code
    );

    const assertion = firebase.auth.PhoneMultiFactorGenerator.assertion(credential);

    await user.multiFactor.enroll(assertion, "Primary Phone");
 
    const multiFactorUser = user.multiFactor
    const enrolledFactors = multiFactorUser.enrolledFactors;

    const phoneFactorToUnenroll = enrolledFactors.find(
      (factor) => factor.factorId === firebase.auth.PhoneAuthProvider.PROVIDER_ID && factor.displayName !== 'My Authenticator App' && factor.phoneNumber !== currentPhone
    );

    if (!phoneFactorToUnenroll) {
      dispatch({ type: 'MFA_UNENROLL_NOT_NEEDED' });
      return;
    }

    await multiFactorUser.unenroll(phoneFactorToUnenroll);


    dispatch(resetResendCount());

    await addPhoneRequest('/users/addPhone', { userId, phone: currentPhone });
    redirect(routes.CHANGE_PHONE_SUCCESS_ROUTE);
  } catch (err) {
    if(err.code === 'auth/requires-recent-login') {
      redirect(`${routes.VALIDATE_PASSWORD_ROUTE}`);
      return;
    }
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError())
  }
}

export const resendCodeMobile = () => (dispatch, getState) => {

  const { phone } = getState().authReducer;

  firebase.auth().useDeviceLanguage();



  firebase.auth().signInWithPhoneNumber(`+${phone}`, getRecaptchaVerifier())
  .then((res) => {
    dispatch(setCurrentCode(res));
    dispatch(addResend());
    redirect(`${routes.TWO_FACTOR_AUTH_CODE_SENT_ROUTE}`);
  })
  .catch((err) => {
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError())
  })
}

export const verifyAuth = () => (dispatch) => {
  firebase.auth().onAuthStateChanged(user => {
    if (!user) {
      //TODO check token expiration time
      dispatch(logoutRequest());
    }
  })
};

export const logoutRequest = () => async (dispatch) => {
  let ip = await getUserIp();

  const user = firebase.auth().currentUser;
  if (user) {
    const email = user ? user?.email : '';
    dispatch(userLogoutRequest());
    firebase.auth().signOut()
      .then(() => {

        dispatch(sendSigninAttemptLog({ email, status: 'success', ip, type: 'logout' }))
        // saveState({});
      })
      .catch(err => {
        dispatch(sendErrorLog(err.message, err.code));
        dispatch(sendSigninAttemptLog({ email, status: 'failure', type: 'logout', ip, failureReason: err.message || '' }))
        dispatch(loginFailure());
      });
  }
};

export const forgotPassword = (email) => (dispatch) => {
  dispatch(forgotPasswordRequest());
  redirect(routes.FORGOT_PASSWORD_EMAIL_SENT_ROUTE);
  apiForgotPassword(email, window.location.origin);
};

export const resetPasswordRequest = ({verificationCode, newPassword}) => async (dispatch) => {
  await dispatch(forgotPasswordRequest());
  const passwordPolicy = await getPasswordPolicy();
  const error = passwordValidationError(newPassword, passwordPolicy);
  if (error) {
    dispatch(forgotPasswordFailure(error));
    return;
  }
  firebase.auth().confirmPasswordReset(verificationCode, newPassword)
    .then(() => redirect(routes.LOGIN_ROUTE))
    .catch(err => {
      dispatch(sendErrorLog(err.message, err.code));
      dispatch(forgotPasswordFailure(getForgotPasswordValidationMessage(err.code)));
    });
};

const updatePassword = (user, newPassword) => (dispatch) => {
  user.updatePassword(newPassword)
    .then(() => {
      dispatch(changePasswordSuccess());
      const dateFormat = "yyyy-MM-dd";
      const formattedDate = dateFns.format(new Date(), dateFormat);
      redirect(`${routes.DASHBOARD_DAY_ROUTE}/${formattedDate}`);
    })
    .catch(err => {
      dispatch(sendErrorLog(err.message, err.code));
      dispatch(changePasswordFailure(getNewPasswordValidationMessage(err.code)));
    });
};

export const getCodeMobilePassword = (oldPassword) => async (dispatch) => {
  firebase.auth().useDeviceLanguage();
  const user = firebase.auth().currentUser;
  const credential = firebase.auth.EmailAuthProvider.credential(user.email, oldPassword);

  try {
    await user.reauthenticateWithCredential(credential);
  } catch (error) {
    if(error.code === 'auth/multi-factor-auth-required') {
      dispatch(setIsInTwoFactor(true));
      firebase.auth().useDeviceLanguage();
      redirect(routes.TWO_FACTOR_AUTH_ROUTE);

      const resolver = error.resolver;
      const hints = resolver.hints;

      const phoneHint = hints.find(
        (hint) => hint.factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID
      );

      if (phoneHint) {
        try {
          const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
          const verifier = getRecaptchaVerifier();
          const verificationId = await phoneAuthProvider.verifyPhoneNumber(
            {
              multiFactorHint: phoneHint,
              session: resolver.session,
            },
            verifier
          );

          dispatch(setCurrentCode({ verificationId, resolver }));
          dispatch(setIsInTwoFactor(true));
          redirect(routes.CHANGE_PASSWORD_ENTER_CODE);

        } catch (smsError) {
          console.error("Error sending SMS:", smsError);
        }
      } else {
        dispatch(changePasswordFailure({newPasswordError: "MFA required, but no phone enrolled."}))
      }
    }
  }
}

export const confirmCodeMobilePassword = (code) => async (dispatch, getState) => {
  const { currentCode, passwords: { oldPassword, newPassword } } = getState().authReducer;

  try {
    const { verificationId, resolver } = currentCode;
    const phoneCredential = firebase.auth.PhoneAuthProvider.credential(verificationId, code);
    const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(phoneCredential);
    const userCredential = await resolver.resolveSignIn(multiFactorAssertion);

    const validationError =
      duplicatePasswordsError(oldPassword, newPassword) ||
      passwordValidationError(newPassword);

    if (validationError) {
      dispatch(changePasswordFailure({ newPasswordError: validationError }));
      return;
    }

    dispatch(updatePassword(userCredential.user, newPassword))

  } catch (err) {
    console.error("MFA confirm error:", err);
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError());
  } finally {
    dispatch(setPasswords({ oldPassword: null, newPassword: null }));
  }
};

export const reAuthenticateUser = ({ password }) => async (dispatch) => {
  const user = firebase.auth().currentUser;


  if (!user) {
    console.error("No user is currently signed in.");
    dispatch({ type: "USER_NOT_SIGNED_IN" });
    return;
  }

  try {
    const credential = firebase.auth.EmailAuthProvider.credential(
      user.email,
      password
    );

    await user.reauthenticateWithCredential(credential);

    dispatch({ type: "USER_REAUTH_SUCCESS" });

    return { success: true };
  } catch (err) {
    if (err.code === "auth/multi-factor-auth-required") {
      const resolver = err.resolver;
      const phoneInfoHint = resolver.hints[0];
      const session = resolver.session;

      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();

      const phoneInfoOptions = {
        multiFactorHint: phoneInfoHint,
        session: session,
      };
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        getRecaptchaVerifier()
      );

      dispatch(setCurrentCode({ verificationId, resolver }));
      dispatch(setIsInTwoFactor(true))
      redirect(`${routes.TWO_FACTOR_AUTH_ROUTE}`);
      firebase.auth().useDeviceLanguage();

      return { success: false, mfaRequired: true, verificationId, resolver };
    }
    dispatch(sendErrorLog(err.message, err.code));
    return { success: false, error: err };
  }
};

export const changePhone = (phone) => async (dispatch) => {
  firebase.auth().useDeviceLanguage();

  const user = firebase.auth().currentUser;

  if (!user) {
    console.error("User is not signed in.");
    return;
  }

  try {

    const provider = new firebase.auth.PhoneAuthProvider();
    const verificationId = await provider.verifyPhoneNumber(`+${phone}`, getRecaptchaVerifier());


    dispatch(setPhone(`+${phone}`));
    dispatch(setCurrentCode(verificationId));
    dispatch(setCurrentCode({ verificationId }));
    redirect(routes.CHANGE_PHONE_ENTER_CODE);
  } catch (err) {
    if (
      err?.message?.includes("auth/too-many-requests") ||
      err?.message?.includes("auth/too-many-attempts") ||
      err?.message?.includes("auth/error-code:-39")
    ) {
      return toast.error(
        "You have reached your OTP confirmation limit for today. Please try again tomorrow.",
        { position: "bottom-right", style: { padding: "15px 20px" } }
      );
    }
    if (err.code === 'auth/requires-recent-login') {
      redirect(`${routes.VALIDATE_PASSWORD_ROUTE}`);
    }
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError());
  }
};

export const changePassword = ({oldPassword, newPassword}) => (dispatch) => {
  dispatch(changePasswordRequest());
  const user = firebase.auth().currentUser;
  if (!user) {
    dispatch(changePasswordFailure("Invalid user"));
    redirect(routes.LOGIN_ROUTE);
    return;
  }

    dispatch(setPasswords({oldPassword, newPassword}));
    const validationError = duplicatePasswordsError(oldPassword, newPassword) || passwordValidationError(newPassword);
    if (validationError) {
      dispatch(changePasswordFailure({newPasswordError: validationError}))
    } else {
      dispatch(getCodeMobilePassword(oldPassword));
    }
    return;
};
